import { createResumeColumns, ResumeActionType } from "@/colDef/resumeColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Spinner,
} from "@/components";
import ResumeDialog from "@/components/formDialog/ResumeDialog";
import {
  useDeleteResumeMutation,
  useResumesQuery,
  useSetPrimaryResumeMutation,
} from "@/hooks/api/profile.queries";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { ResumeType } from "@/services/profile.service";
import { Upload } from "lucide-react";
import { useCallback, useMemo } from "react";

export default function Resumes() {
  const { data: resumes, isLoading, isError, refetch } = useResumesQuery();
  const deleteMutation = useDeleteResumeMutation();
  const setPrimaryMutation = useSetPrimaryResumeMutation();

  const deleteDialog = useAlertDialog();
  const resumeDialog = useFormDialog({
    mode: "create" as "create",
  });

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: ResumeActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to completely remove this resume? It will no longer be available when applying to internal internships.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.id,
          action: "delete",
        });
      } else if (payload.action === "set_primary") {
        setPrimaryMutation.mutate(payload.id);
      }
    },
    [deleteDialog, setPrimaryMutation]
  );

  const columns = useMemo(
    () => createResumeColumns(handleActionClick),
    [handleActionClick]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load resumes."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Document Hub: Resumes</h1>
        <Button
          onClick={() => resumeDialog.open({})}
          className="flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700 shadow-md"
        >
          <Upload size={16} />
          Upload New Resume
        </Button>
      </div>

      <Card className="shadow-lg border-t-4 border-t-indigo-600">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<ResumeType, any>
              columns={columns}
              data={resumes || []}
            />
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <ResumeDialog
        isOpen={resumeDialog.state.isOpen}
        onOpenChange={(open) => !open && resumeDialog.close()}
        onCancel={resumeDialog.close}
      />

      {/* Delete Confirmation */}
      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Delete Resume"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
}
